import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import type { GrnView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  PageHeader,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
} from '@wodi/ui';
import { useState } from 'react';

import {
  decideGrnReversal,
  grnDetailQuery,
  grnReversalQuery,
  postedGrnsQuery,
  requestGrnReversal,
} from '../api/procurement.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { formatInstant, formatIsoDate } from '../patients/format.ts';
import { LoadFailed, OfflineNote, ReasonDialog } from '../procurement/common.tsx';
import { documentLabel, documentTag } from '../procurement/format.ts';
import { BackToStore, WithStore } from '../stock/common.tsx';
import { formatCents, formatQuantity } from '../stock/format.ts';
import styles from '../stock/stock.module.css';
import { toasts } from '../toasts.ts';

// Goods received notes of a store, and reversing one (docs/plans/phase2-procurement.md §2.7,
// §5.8). A reversal is asked for with a reason and approved by another person; approval
// posts opposite ledger movements, and the GRN itself never changes.

const listRoute = getRouteApi('/_app/stores/$storeId/receipts');
const detailRoute = getRouteApi('/_app/stores/$storeId/receipts/$grnId');

export function GoodsReceivedPage() {
  const { storeId } = listRoute.useParams();
  return (
    <WithStore storeId={storeId}>
      {(store) => <GrnList storeId={store.id} storeName={store.name} />}
    </WithStore>
  );
}

function GrnList({ storeId, storeName }: { storeId: string; storeName: string }) {
  const grns = useQuery(postedGrnsQuery(storeId));
  return (
    <div className={styles.page}>
      <BackToStore storeId={storeId} name={storeName} />
      <PageHeader title={`Goods received into ${storeName}`} />
      <Section title="Posted goods received notes">
        {grns.isPending ? <LoadingBlock label="Loading goods received" rows={4} /> : null}
        {grns.isError ? (
          <LoadFailed
            error={grns.error}
            fallback="Goods received couldn't be loaded."
            onRetry={() => void grns.refetch()}
          />
        ) : null}
        {grns.isSuccess ? (
          <TableScroll>
            <Table flat aria-label={`Goods received into ${storeName}`}>
              <TableHeader>
                <Column isRowHeader>GRN</Column>
                <Column>Received</Column>
                <Column>Supplier</Column>
                <Column>LPO</Column>
                <Column numeric>Total</Column>
                <Column>State</Column>
              </TableHeader>
              <TableBody
                items={grns.data.items}
                renderEmptyState={() => 'Nothing received into this store yet.'}
              >
                {(g) => (
                  <Row id={g.id}>
                    <Cell>
                      <Link to="/stores/$storeId/receipts/$grnId" params={{ storeId, grnId: g.id }}>
                        <span className="wodi-id">{g.number}</span>
                      </Link>
                    </Cell>
                    <Cell>{formatInstant(g.receivedAt)}</Cell>
                    <Cell>{g.supplierName}</Cell>
                    <Cell>
                      {g.purchaseOrder === null ? (
                        ''
                      ) : (
                        <span className="wodi-id">{g.purchaseOrder.number}</span>
                      )}
                    </Cell>
                    <Cell numeric>{formatCents(g.total.cents)}</Cell>
                    <Cell>
                      <GrnState grn={g} />
                    </Cell>
                  </Row>
                )}
              </TableBody>
            </Table>
          </TableScroll>
        ) : null}
      </Section>
    </div>
  );
}

function GrnState({ grn }: { grn: Pick<GrnView, 'status' | 'reversal'> }) {
  if (grn.reversal?.status === 'approved') return <Tag state="neutral">Reversed</Tag>;
  if (grn.reversal?.status === 'requested')
    return <Tag state="attention">Reversal waiting for approval</Tag>;
  return <Tag state="done">Posted</Tag>;
}

export function GoodsReceivedNotePage() {
  const { storeId, grnId } = detailRoute.useParams();
  const grn = useQuery(grnDetailQuery(grnId));
  if (grn.isPending) return <LoadingBlock label="Loading the goods received note" rows={4} />;
  if (grn.isError)
    return (
      <LoadFailed
        error={grn.error}
        fallback="The goods received note couldn't be loaded."
        onRetry={() => void grn.refetch()}
      />
    );
  return (
    <WithStore storeId={storeId}>
      {(store) => <GrnDetail grn={grn.data} storeName={store.name} />}
    </WithStore>
  );
}

function GrnDetail({ grn, storeName }: { grn: GrnView; storeName: string }) {
  return (
    <div className={styles.page}>
      <p className={styles.meta}>
        <Link to="/stores/$storeId/receipts" params={{ storeId: grn.storeId }}>
          Back to goods received into {storeName}
        </Link>
      </p>
      <PageHeader title={`Goods received note ${grn.number}`} />
      <Section title={grn.supplierName} actions={<GrnState grn={grn} />}>
        <dl className={styles.facts}>
          <dt>GRN</dt>
          <dd>
            <span className="wodi-id">{grn.number}</span>
          </dd>
          <dt>Received</dt>
          <dd>{formatInstant(grn.receivedAt)}</dd>
          {grn.supplierDocumentNumber === null ? null : (
            <>
              <dt>Their document</dt>
              <dd>
                <span className="wodi-id">{grn.supplierDocumentNumber}</span>
              </dd>
            </>
          )}
          {grn.purchaseOrder === null ? null : (
            <>
              <dt>LPO</dt>
              <dd>
                <Link to="/procurement/orders/$orderId" params={{ orderId: grn.purchaseOrder.id }}>
                  <span className="wodi-id">{grn.purchaseOrder.number}</span>
                </Link>
              </dd>
            </>
          )}
          <dt>Total</dt>
          <dd className={styles.numbers}>{formatCents(grn.total.cents)}</dd>
        </dl>
        {grn.status === 'posted' ? <Reversal grn={grn} /> : null}
      </Section>
      <Section title="Lines">
        <TableScroll>
          <Table flat aria-label={`Lines of ${grn.number}`}>
            <TableHeader>
              <Column isRowHeader>Item</Column>
              <Column>Batch</Column>
              <Column>Expiry</Column>
              <Column numeric>Packs</Column>
              <Column numeric>Quantity</Column>
              <Column numeric>Line cost</Column>
            </TableHeader>
            <TableBody items={grn.lines.map((l) => ({ ...l, id: l.lineNo }))}>
              {(l) => (
                <Row id={l.lineNo}>
                  <Cell>
                    {l.itemName}
                    {l.overReceiptReason === null
                      ? ''
                      : ` (more than ordered: ${l.overReceiptReason})`}
                  </Cell>
                  <Cell>
                    <span className="wodi-id">{l.batchNumber}</span>
                  </Cell>
                  <Cell>{l.expiryDate === null ? 'No expiry' : formatIsoDate(l.expiryDate)}</Cell>
                  <Cell numeric>
                    {l.packs} × {l.packUnitDisplay}
                  </Cell>
                  <Cell numeric>{formatQuantity(l.quantity, '').trim()}</Cell>
                  <Cell numeric>{formatCents(l.lineCost.cents)}</Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      </Section>
    </div>
  );
}

type Decision = 'approve' | 'reject' | 'cancel';
const WORDS: Record<Decision | 'request', { confirm: string; toast: string }> = {
  request: { confirm: 'Ask for reversal', toast: 'Reversal requested' },
  approve: { confirm: 'Reverse GRN', toast: 'GRN reversed' },
  reject: { confirm: 'Reject reversal', toast: 'Reversal rejected' },
  cancel: { confirm: 'Cancel reversal', toast: 'Reversal cancelled' },
};

function Reversal({ grn }: { grn: GrnView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const queryClient = useQueryClient();
  const live = grn.reversal?.status === 'requested' ? grn.reversal : null;
  const reversal = useQuery({ ...grnReversalQuery(live?.id ?? ''), enabled: live !== null });
  const [acting, setActing] = useState<Decision | 'request' | null>(null);
  const act = useMutation({
    mutationFn: ({ action, reason }: { action: Decision | 'request'; reason: string }) =>
      action === 'request'
        ? requestGrnReversal(grn.id, reason)
        : decideGrnReversal(live?.id ?? '', action, reason),
    onSuccess: async (_result, { action }) => {
      await queryClient.invalidateQueries({ queryKey: ['grn'] });
      await queryClient.invalidateQueries({ queryKey: ['grn-reversal'] });
      await queryClient.invalidateQueries({ queryKey: ['stock', grn.storeId] });
      await queryClient.invalidateQueries({ queryKey: ['procurement'] });
      toasts.show({ title: WORDS[action].toast, description: grn.number });
      setActing(null);
    },
  });
  if (session === null) return null;
  const view = reversal.data;
  return (
    <>
      {grn.reversal === null ? null : (
        <p className={styles.meta}>
          Reversal {documentLabel(grn.reversal.status).toLowerCase()}: {grn.reversal.reason}{' '}
          <Tag state={documentTag(grn.reversal.status)}>{documentLabel(grn.reversal.status)}</Tag>
        </p>
      )}
      <div className={styles.actions}>
        {grn.reversal?.status !== 'approved' && live === null && can(session, 'stock.receive') ? (
          <Button
            isDisabled={!online}
            onPress={() => {
              setActing('request');
            }}
          >
            Reverse GRN
          </Button>
        ) : null}
        {view?.canDecide === true ? (
          <>
            <Button
              variant="primary"
              isDisabled={!online}
              onPress={() => {
                setActing('approve');
              }}
            >
              Approve reversal
            </Button>
            <Button
              isDisabled={!online}
              onPress={() => {
                setActing('reject');
              }}
            >
              Reject reversal
            </Button>
          </>
        ) : null}
        {view?.status === 'requested' && view.requestedBy.id === session.user.id ? (
          <>
            <span className={styles.meta}>
              Another person with approval rights must approve this.
            </span>
            <Button
              variant="quiet"
              isDisabled={!online}
              onPress={() => {
                setActing('cancel');
              }}
            >
              Cancel reversal
            </Button>
          </>
        ) : null}
      </div>
      {reversal.isError ? (
        <LoadFailed
          error={reversal.error}
          fallback="The reversal couldn't be loaded."
          onRetry={() => void reversal.refetch()}
        />
      ) : null}
      <OfflineNote what="Reversing a GRN" />
      {acting === null ? null : (
        <ReasonDialog
          title={`${WORDS[acting].confirm}: ${grn.number}`}
          confirmLabel={WORDS[acting].confirm}
          isPending={act.isPending}
          error={act.error}
          onConfirm={(reason) => {
            act.mutate({ action: acting, reason });
          }}
          onClose={() => {
            setActing(null);
            act.reset();
          }}
        >
          {acting === 'request' ? (
            <p>
              Everything on this GRN comes back out of the store once another person approves. It
              can't be reversed if any of it has already been issued or moved.
            </p>
          ) : null}
        </ReasonDialog>
      )}
    </>
  );
}
